import { db } from "../firebaseClient"
import { doc, setDoc, getDoc, increment, serverTimestamp, collection, getDocs } from "firebase/firestore"

// One doc per org per day, keyed by date string — cheap to write from any API
// call site, cheap to aggregate over a week without a query. Call logApiRequest
// from wherever your API layer actually handles requests (e.g. GOA-style Render
// service, or a Cloud Function) once that's wired up.
const dateKey = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`

export const logApiRequest = async (orgId, { isError } = {}) => {
  if (!orgId) return { error: null }
  try {
    const ref = doc(db, "organizations", orgId, "apiMetrics", dateKey())
    await setDoc(
      ref,
      {
        date: dateKey(),
        requests: increment(1),
        errors: increment(isError ? 1 : 0),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    )
    return { error: null }
  } catch (error) {
    return { error }
  }
}
// Lists every daily metrics doc for an org — used for all-time totals rather
// than the rolling N-day window getApiHealthSummary computes.
export const listOrgApiMetrics = async (orgId) => {
  try {
    const ref = collection(db, "organizations", orgId, "apiMetrics")
    const snap = await getDocs(ref)
    return { metrics: snap.docs?.map((d) => d.data()), error: null }
  } catch (error) {
    return { metrics: [], error }
  }
}

// Aggregates the last N days of metrics (default 7) into a single error rate.
// No docs yet for a given day is a legitimate zero, not missing data.
export const getApiHealthSummary = async (orgId, days = 7) => {
  if (!orgId) return { totalRequests: 0, totalErrors: 0, errorRate: 0, error: null }
  try {
    const today = new Date()
    const reads = Array.from({ length: days })?.map((_, i) => {
      const d = new Date(today)
      d.setDate(d.getDate() - i)
      return getDoc(doc(db, "organizations", orgId, "apiMetrics", dateKey(d)))
    })
    const snaps = await Promise.all(reads)
    let totalRequests = 0
    let totalErrors = 0
    snaps?.forEach((snap) => {
      if (snap.exists()) {
        totalRequests += snap.data()?.requests || 0
        totalErrors += snap.data()?.errors || 0
      }
    })
    const errorRate = totalRequests > 0 ? Math.round((totalErrors / totalRequests) * 1000) / 10 : 0
    return { totalRequests, totalErrors, errorRate, error: null }
  } catch (error) {
    return { totalRequests: 0, totalErrors: 0, errorRate: 0, error }
  }
}